import { IWordbook, IVocab } from "@/common/types/wordbank";

// Define basket item type that can be either IVocab or IWordbook
export type BasketItem = (IWordbook & { itemType: "wordbook" }) | (IVocab & { itemType: "vocab" });

// Define the state type
export interface VocabBasketState {
  items: BasketItem[];
  questionId?: string; // Add questionId to track which question the basket belongs to
}

// Action types
export const VOCAB_BASKET_ADD = "VOCAB_BASKET_ADD";
export const VOCAB_BASKET_REMOVE = "VOCAB_BASKET_REMOVE";
export const VOCAB_BASKET_CLEAR = "VOCAB_BASKET_CLEAR";
export const VOCAB_BASKET_SET_QUESTION = "VOCAB_BASKET_SET_QUESTION";
export const VOCAB_BASKET_CLEAR_AND_EXIT = "VOCAB_BASKET_CLEAR_AND_EXIT";

// Load initial state
// Uses questionId if provided to create question-specific baskets
const getInitialState = (questionId?: string): VocabBasketState => {
  return {
    items: [],
    questionId,
  };
};

// Reducer
export const VocabBasketReducer = (state = getInitialState(), action: any): VocabBasketState => {
  // Helper function to log the current state and action
  const logAction = (actionType: string, data?: any) => {
    // console.log(`[VocabBasket Reducer] ${actionType}`, {
    //     state: { ...state },
    //     data,
    //     resultingItems: data?.items || state.items
    // });
  };

  switch (action.type) {
    case VOCAB_BASKET_SET_QUESTION:
      // When setting a new question context, we should only update the questionId
      // and keep the existing items if the questionId is the same
      logAction(VOCAB_BASKET_SET_QUESTION, { payload: action.payload });

      // If it's the same questionId, don't clear the items
      if (state.questionId === action.payload) {
        return state;
      }

      // Only clear items if we're changing to a different question
      const newState = {
        items: [],
        questionId: action.payload,
      };
      logAction(VOCAB_BASKET_SET_QUESTION, { result: newState });
      return newState;

    case VOCAB_BASKET_ADD:
      // Check if item already exists in the basket
      logAction(VOCAB_BASKET_ADD, { payload: action.payload });

      const exists = state.items.some(
        (item: BasketItem) => item._id === action.payload._id && item.itemType === action.payload.itemType
      );
      if (exists) {
        return state;
      }
      const newItems = [...state.items, action.payload];
      const addResult = {
        ...state,
        items: newItems,
      };
      logAction(VOCAB_BASKET_ADD, { result: addResult });
      return addResult;

    case VOCAB_BASKET_REMOVE:
      logAction(VOCAB_BASKET_REMOVE, { payload: action.payload });

      const filteredItems = state.items.filter(
        (item: BasketItem) => !(item._id === action.payload.id && item.itemType === action.payload.itemType)
      );
      const removeResult = {
        ...state,
        items: filteredItems,
      };
      logAction(VOCAB_BASKET_REMOVE, { result: removeResult });
      return removeResult;

    case VOCAB_BASKET_CLEAR:
      logAction(VOCAB_BASKET_CLEAR);

      const clearResult = {
        ...state,
        items: [],
      };
      logAction(VOCAB_BASKET_CLEAR, { result: clearResult });
      return clearResult;

    case VOCAB_BASKET_CLEAR_AND_EXIT:
      logAction(VOCAB_BASKET_CLEAR_AND_EXIT);

      const clearAndExitResult = {
        items: [],
        questionId: undefined,
      };
      logAction(VOCAB_BASKET_CLEAR_AND_EXIT, { result: clearAndExitResult });
      return clearAndExitResult;
    default:
      return state;
  }
};

// Action creators
export const setVocabBasketQuestion = (questionId: string) => ({
  type: VOCAB_BASKET_SET_QUESTION,
  payload: questionId,
});

export const addToVocabBasket = (item: IVocab | IWordbook, itemType: "vocab" | "wordbook") => ({
  type: VOCAB_BASKET_ADD,
  payload: { ...item, itemType },
});

export const removeFromVocabBasket = (id: string, itemType: "vocab" | "wordbook") => ({
  type: VOCAB_BASKET_REMOVE,
  payload: { id, itemType },
});

export const clearVocabBasket = () => ({
  type: VOCAB_BASKET_CLEAR,
});

export const clearAndExitVocabBasket = () => ({
  type: VOCAB_BASKET_CLEAR_AND_EXIT,
});
